import React, { useState } from "react";
import {
  Table,
  Pagination,
  Avatar,
  Dropdown,
  Menu,
  Modal,
  Button,
  message,
} from "antd";
import {
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { Vendor, useVendors } from "../../hooks/useVendors";

interface VendorTableProps {
  filters: Record<string, any>;
  onModifyVendor: (vendor: Vendor) => void;
  onDeleteVendor: (vendorId: number) => void;
  canModifyVendors: boolean;
  canDeleteVendors: boolean;
}

const VendorTable: React.FC<VendorTableProps> = ({
  filters,
  onModifyVendor,
  onDeleteVendor,
  canModifyVendors,
  canDeleteVendors,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data: vendorsData, isLoading: vendorsLoading } = useVendors(
    currentPage,
    filters
  );

  const handleDelete = (vendorId: number) => {
    if (!canDeleteVendors) {
      message.error("You don't have permission to delete vendors");
      return;
    }

    Modal.confirm({
      title: "Delete Vendor",
      content: "Are you sure you want to delete this vendor?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => onDeleteVendor(vendorId),
    });
  };

  const handleModify = (vendor: Vendor) => {
    if (!canModifyVendors) {
      message.error("You don't have permission to modify vendors");
      return;
    }

    onModifyVendor(vendor);
  };

  // Generate menu items based on permissions
  const generateMenuItems = (record: Vendor) => {
    const menuItems = [];

    if (canModifyVendors) {
      menuItems.push(
        <Menu.Item
          key="modify"
          icon={<EditOutlined />}
          onClick={() => handleModify(record)}
        >
          Modify
        </Menu.Item>
      );
    }

    if (canDeleteVendors) {
      menuItems.push(
        <Menu.Item
          key="delete"
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
          danger
        >
          Delete
        </Menu.Item>
      );
    }

    return menuItems;
  };

  // Generate columns and conditionally add actions
  const getColumns = (): ColumnsType<Vendor> => {
    const baseColumns: ColumnsType<Vendor> = [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        fixed: "left",
        render: (name, record) => (
          <div className="flex items-center space-x-2">
            <Avatar src={record.avatar} />
            <span>{name}</span>
          </div>
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        ellipsis: true,
      },
      {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
        ellipsis: true,
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        render: (location) => location?.name || "Unknown",
      },
      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        render: (created_at) => new Date(created_at).toLocaleDateString(),
      },
    ];

    // Only add action column if user has either modify or delete permissions
    if (canModifyVendors || canDeleteVendors) {
      baseColumns.push({
        title: "Action",
        key: "action",
        fixed: "right",
        render: (_, record) => {
          const menuItems = generateMenuItems(record);

          // If no permissions, don't show dropdown
          if (menuItems.length === 0) {
            return null;
          }

          return (
            <Dropdown overlay={<Menu>{menuItems}</Menu>} trigger={["click"]}>
              <Button type="text" icon={<EllipsisOutlined />} />
            </Dropdown>
          );
        },
      });
    }

    return baseColumns;
  };

  const columns = getColumns();

  return (
    <div>
      <Table
        dataSource={vendorsData?.data ?? []}
        columns={columns}
        pagination={false}
        loading={vendorsLoading}
        bordered
        scroll={{ x: "max-content" }}
        className="custom-table"
      />

      <div className="flex justify-end mt-4">
        <Pagination
          current={vendorsData?.meta.current_page ?? 1}
          total={vendorsData?.meta.total ?? 0}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
};

export default VendorTable;
